<template>
    <section>
        <div class="sm:px-0">
            <h2 class="text-gray-900 dark:text-slate-200 text-lg font-medium leading-6">Recent Token Activity</h2>
            <!-- Filters -->
            <div v-if="transactionType !== 'all'" class="flex items-center my-2 space-x-3">
                <div class="text-gray-700 text-sm">Filters:</div>
                <span class="inline-flex items-center pl-2.5 pr-1 py-0.5 text-indigo-700 text-sm bg-indigo-100 rounded-full">
                    {{ transactionType }}
                    <Link
                        :href="route('wallet.user_token_activity')"
                        :only="['tokenActivity']"
                        class="has-tooltip inline-flex flex-shrink-0 items-center justify-center ml-0.5 w-4 h-4 text-indigo-400 hover:text-indigo-500 focus:text-white hover:bg-indigo-200 focus:bg-indigo-500 rounded-full focus:outline-none"
                        preserve-scroll
                        preserve-state
                    >
                        <span class="sr-only">Remove Filter</span>
                        <svg class="w-2 h-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                            <path stroke-linecap="round" stroke-width="1.5" d="M1 1l6 6m0-6L1 7" />
                        </svg>
                        <tooltip>Remove Filter</tooltip>
                    </Link>
                </span>
            </div>
        </div>

        <!-- Activity list (smallest breakpoint only) -->
        <div class="sm:shadow sm:hidden">
            <ul class="mt-2 divide-gray-200 dark:divide-slate-700/75 divide-y overflow-hidden sm:hidden -mx-2 sm:mx-0">
                <li v-for="transaction in transactions" :key="transaction.id">
                    <a href="#" class="block px-4 py-4 hover:bg-gray-50 bg-white">
                        <span class="flex items-center space-x-4">
                            <span class="flex flex-1 space-x-2 truncate">
                                <svg v-if="transaction.type === 'deposit'" class="flex-shrink-0 w-5 h-5 text-gray-600 group-hover:text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                </svg>
                                <svg v-if="transaction.pledge" class="flex-shrink-0 w-5 h-5 text-gray-600 group-hover:text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
                                </svg>
                                <svg v-if="transaction.post" class="flex-shrink-0 w-5 h-5 text-gray-600 group-hover:text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
                                </svg>
                                <span class="flex flex-col text-gray-500 text-sm truncate">
                                    <span v-if="transaction.type === 'deposit'" class="text-gray-800 truncate">Purchased {{ transaction.amount | toLocaleString }} tokens</span>
                                    <span v-if="transaction.pledge" class="text-gray-800 truncate">Pledged {{ transaction.amount | toLocaleString }} tokens to Challenge #{{ transaction.pledge.id }}</span>
                                    <span v-if="transaction.post" class="text-gray-800 truncate">Published Challenge #{{ transaction.post.id }}</span>
                                    <time :datetime="transaction.createdAt">{{ transaction.createdAt | dateFormat("MMMM D, YYYY h:mm A") }}</time>
                                </span>
                            </span>
                            <!-- Heroicon name: solid/chevron-right -->
                            <svg class="flex-shrink-0 w-5 h-5 text-gray-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                            </svg>
                        </span>
                    </a>
                </li>

                <li v-if="!transactions.length" class="bg-white">
                    <no-results :show="true">
                        <span class="capitalize">No Transactions</span>
                    </no-results>
                </li>
            </ul>

            <nav v-if="transactions.length" class="flex items-center justify-between px-4 py-3 -mx-2 sm:mx-0 bg-white border-t border-gray-200" aria-label="Pagination">
                <div class="flex flex-1 items-center justify-between">
                    <Link
                        :href="paginate.prev_page_url || ''"
                        as="button"
                        :only="['token_activity']"
                        :disabled="!paginate.prev_page_url"
                        class="relative inline-flex flex-1 items-center justify-center px-4 py-2 hover:text-gray-500 text-gray-700 text-sm font-medium bg-white border border-gray-300 rounded-md"
                        preserve-state
                        preserve-scroll
                    >
                        Previous</Link
                    >
                    <span class="flex-shrink-0 mx-2 px-5 text-gray-700">{{ paginate.current_page }} / {{ paginate.last_page }}</span>
                    <Link
                        :href="paginate.next_page_url || ''"
                        as="button"
                        :only="['token_activity']"
                        :disabled="!paginate.next_page_url"
                        class="relative inline-flex flex-1 items-center justify-center px-4 py-2 hover:text-gray-500 text-gray-700 text-sm font-medium bg-white border border-gray-300 rounded-md"
                        preserve-state
                        preserve-scroll
                    >
                        Next</Link
                    >
                </div>
            </nav>
        </div>

        <!-- Activity table (small breakpoint and up) -->
        <div class="hidden sm:block">
            <div class="flex flex-col mt-2">
                <div class="align-middle min-w-full bg-white dark:bg-slate-800 rounded-lg shadow overflow-hidden overflow-x-auto">
                    <table v-if="transactions.length" class="min-w-full divide-gray-200 dark:divide-slate-700/75 divide-y">
                        <thead>
                            <tr>
                                <th class="px-6 py-3 text-left text-gray-700 dark:text-gray-400 text-xs font-medium tracking-wider bg-white dark:bg-slate-800 uppercase">Transaction</th>
                                <th class="px-6 py-3 text-left text-gray-700 dark:text-gray-400 text-xs font-medium tracking-wider bg-white dark:bg-slate-800 uppercase">Amount</th>
                                <th class="hidden px-6 py-3 text-left text-gray-700 dark:text-gray-400 text-xs font-medium tracking-wider bg-white dark:bg-slate-800 uppercase md:block">Type</th>
                                <th class="px-6 py-3 text-left text-gray-700 dark:text-gray-400 text-xs font-medium tracking-wider bg-white dark:bg-slate-800 uppercase">Date</th>
                            </tr>
                        </thead>
                        <tbody class="divide-gray-100 dark:divide-slate-700/75 divide-y">
                            <tr v-for="transaction in transactions" :key="transaction.id" class="bg-white dark:bg-slate-800">
                                <td class="px-6 py-3 w-full max-w-0 text-gray-900 dark:text-slate-200 whitespace-nowrap text-sm">
                                    <div class="flex">
                                        <!-- Purchased -->
                                        <div class="has-tooltip">
                                            <Link :href="transaction.stripe_id ? route('wallet.user_token_transaction', transaction.id) : ''" v-if="transaction.type === 'deposit'" class="group inline-flex text-sm space-x-2 truncate">
                                                <svg class="flex-shrink-0 w-5 h-5 text-gray-600 dark:text-slate-200 group-hover:text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                                </svg>
                                                <p class="text-gray-600 dark:text-slate-200 group-hover:text-indigo-600 truncate">You purchased {{ transaction.amount | toLocaleString }} tokens</p>
                                            </Link>
                                            <tooltip v-if="!transaction.stripe_id">Transaction Not Found</tooltip>
                                        </div>
                                        <!-- Pledged -->
                                        <Link :href="transaction.stripe_id ? route('wallet.user_token_transaction', transaction.id) : ''" v-if="transaction.type === 'withdraw' && transaction.pledge" class="group inline-flex text-sm space-x-2 truncate">
                                            <svg class="flex-shrink-0 w-5 h-5 text-gray-600 dark:text-slate-200 group-hover:text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
                                            </svg>
                                            <p class="text-gray-600 dark:text-slate-200 group-hover:text-indigo-600 truncate">You pledged {{ transaction.amount | toLocaleString }} tokens to Challenge #{{ transaction.pledge.id }}</p>
                                        </Link>
                                        <!-- Published -->
                                        <Link :href="transaction.stripe_id ? route('wallet.user_token_transaction', transaction.id) : ''" v-if="transaction.type === 'withdraw' && transaction.post" class="group inline-flex text-sm space-x-2 truncate">
                                            <svg class="flex-shrink-0 w-5 h-5 text-gray-600 dark:text-slate-200 group-hover:text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
                                            </svg>
                                            <p class="text-gray-600 dark:text-slate-200 group-hover:text-indigo-600 truncate">You published Challenge #{{ transaction.post.id }}</p>
                                        </Link>
                                    </div>
                                </td>
                                <td class="px-6 py-2 text-left text-gray-600 dark:text-slate-200 whitespace-nowrap text-sm">
                                    <span class="font-medium">{{ transaction.amount | currency }}</span>
                                </td>
                                <td class="hidden px-6 py-2 text-gray-600 dark:text-slate-200 whitespace-nowrap text-sm md:block">
                                    <Link
                                        :href="route('wallet.user_token_activity', { type: 'deposits' })"
                                        v-if="transaction.type === 'deposit'"
                                        class="has-tooltip inline-flex items-center px-2.5 py-0.5 hover:text-green-700 text-green-800 text-xs hover:bg-green-100 bg-green-200 rounded-md"
                                        preserve-state
                                    >
                                        deposit
                                        <tooltip>Filter by</tooltip>
                                    </Link>
                                    <Link
                                        :href="route('wallet.user_token_activity', { type: 'withdrawals' })"
                                        v-if="transaction.type === 'withdraw'"
                                        class="has-tooltip inline-flex items-center px-2.5 py-0.5 hover:text-red-700 text-red-800 text-xs hover:bg-red-100 bg-red-200 rounded-md"
                                        preserve-state
                                    >
                                        withdrawal
                                        <tooltip>Filter by</tooltip>
                                    </Link>
                                </td>
                                <td class="px-6 py-2 text-left text-gray-600 dark:text-slate-200 whitespace-nowrap text-sm">
                                    <time :datetime="transaction.createdAt">{{ transaction.createdAt | dateFormat("MMM D, h:mm A") }}</time>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <!-- Pagination -->
                    <div class="px-4 py-3 bg-white dark:bg-slate-800 sm:px-6">
                        <pagination :data="paginate" only="tokenActivity" />
                    </div>
                    <no-results :show="!transactions.length">
                        <span class="capitalize">No Transactions</span>
                    </no-results>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import Pagination from "@/Components/Pagination"
import Tooltip from "@/Components/Tooltip"
import NoResults from "@/Jetstream/NoResults"

export default {
    components: { NoResults, Tooltip, Pagination },

    props: {
        activity: {
            required: true,
        },
    },

    computed: {
        transactions() {
            return this.activity.paginate.data
        },

        paginate() {
            return this.activity.paginate
        },

        transactionType() {
            return this.activity.type
        },
    },
}
</script>
